import React, { useState, useEffect } from "react";
import {
  CalendarDays,
  MapPin,
  Ticket,
  ScanLine,
  Pencil,
  Trash2,
  Loader2,
  BadgeDollarSign,
  Layers3,
  Plus,
} from "lucide-react";
import TicketList from "./TicketList";
import TicketScanner from "./TicketScanner";
import EditEventModal from "./EditEventModal";

export default function ManageEvents({ userEmail, onCreateEvent, onManageMerch }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketsByEvent, setTicketsByEvent] = useState({});
  const [selectedEventIdForTickets, setSelectedEventIdForTickets] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  useEffect(() => {
    const fetchMyEvents = async () => {
      try {
        const res = await fetch(
          `/api/myEvents?email=${encodeURIComponent(userEmail)}`
        );
        const data = await res.json();

        if (res.ok && data.success) {
          setEvents(data.events);
        } else {
          alert("Failed to load your events");
        }
      } catch (err) {
        console.error(err);
        alert("Error loading your events.");
      } finally {
        setLoading(false);
      }
    };

    if (userEmail) {
      fetchMyEvents();
    } else {
      setLoading(false);
    }
  }, [userEmail]);

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this event? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      const res = await fetch(`/api/deleteEvent/${id}`, { method: "DELETE" });
      const data = await res.json();

      if (res.ok && data.success) {
        setEvents((prev) => prev.filter((e) => e._id !== id));
        alert("Event deleted.");
      } else {
        alert("Failed to delete event.");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting event.");
    }
  };

  const handleViewTickets = async (eventId) => {
    try {
      const res = await fetch(`/api/ticketsForEvent/${eventId}`);
      const data = await res.json();

      if (res.ok && data.success) {
        setTicketsByEvent((prev) => ({
          ...prev,
          [eventId]: data.tickets,
        }));
      } else {
        alert("Failed to fetch tickets.");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching tickets.");
    }
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return dateString;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  const getTicketTiers = (event) => {
    if (Array.isArray(event.ticketTiers) && event.ticketTiers.length > 0) {
      return event.ticketTiers;
    }

    if (
      typeof event.numTickets !== "undefined" &&
      typeof event.price !== "undefined"
    ) {
      return [
        {
          title: "General Admission",
          price: Number(event.price),
          quantity: Number(event.numTickets),
        },
      ];
    }

    return [];
  };

  const getTierStats = (event) => {
    const ticketTiers = getTicketTiers(event);
    const soldTickets = ticketsByEvent[event._id] || [];

    return ticketTiers.map((tier) => {
      const sold = soldTickets.filter((ticket) => {
        const ticketTierTitle = ticket.ticketTierTitle || "General Admission";
        return ticketTierTitle === tier.title;
      }).length;

      const remaining = Math.max(Number(tier.quantity) - sold, 0);

      return {
        ...tier,
        sold,
        remaining,
        isSoldOut: remaining <= 0,
      };
    });
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-10 shadow-sm">
            <div className="flex items-center gap-3 text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">Loading your events...</span>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Event Management
              </h1>
              <p className="mt-2 text-base text-slate-600">
                Manage your created events, tickets, and event operations in one
                place.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="inline-flex items-center self-start rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm ring-1 ring-slate-200">
                {events.length} {events.length === 1 ? "event" : "events"}
              </div>

              {onManageMerch && (
                <button
                  onClick={onManageMerch}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 ring-1 ring-slate-200 transition hover:bg-slate-50"
                >
                  <Layers3 className="h-4 w-4" />
                  Manage Merch
                </button>
              )}

              <button
                onClick={onCreateEvent}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Create Event
              </button>
            </div>
          </div>
        </header>

        {events.length === 0 ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white px-8 py-16 text-center shadow-sm">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              No events yet
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
              You haven’t created any events yet. Once you do, they’ll appear
              here for editing, ticket management, and scanning.
            </p>

            <button
              onClick={onCreateEvent}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Create your first event
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {events.map((event) => {
              const isExpanded = selectedEventIdForTickets === event._id;
              const ticketTiers = getTicketTiers(event);
              const tierStats = getTierStats(event);

              const totalInventory = tierStats.reduce(
                (sum, tier) => sum + Number(tier.quantity || 0),
                0
              );
              const totalSold = tierStats.reduce(
                (sum, tier) => sum + tier.sold,
                0
              );
              const totalRemaining = tierStats.reduce(
                (sum, tier) => sum + tier.remaining,
                0
              );
              const lowestPrice =
                tierStats.length > 0
                  ? Math.min(...tierStats.map((tier) => Number(tier.price)))
                  : 0;

              return (
                <section
                  key={event._id}
                  className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm"
                >
                  <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
                    <div className="relative min-h-[240px] bg-slate-100">
                      {event.image ? (
                        <img
                          src={event.image}
                          alt={event.name}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-100" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-slate-900/10 to-transparent" />
                    </div>

                    <div className="p-6 sm:p-8">
                      <div className="flex h-full flex-col">
                        <div>
                          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                            {event.name}
                          </h2>

                          <div className="mt-5 space-y-3">
                            <div className="flex items-start gap-3 text-sm text-slate-600">
                              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                              <p>{event.location}</p>
                            </div>

                            <div className="flex items-start gap-3 text-sm text-slate-600">
                              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                              <p>{formatEventDate(event.dateTime)}</p>
                            </div>

                            <div className="flex items-start gap-3 text-sm text-slate-600">
                              <Layers3 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                              <p>
                                {ticketTiers.length}{" "}
                                {ticketTiers.length === 1
                                  ? "ticket tier"
                                  : "ticket tiers"}
                              </p>
                            </div>

                            <div className="flex items-start gap-3 text-sm text-slate-600">
                              <BadgeDollarSign className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                              <p>Starting at ${lowestPrice}</p>
                            </div>

                            <div className="flex items-start gap-3 text-sm text-slate-600">
                              <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                              <p>
                                {totalSold} sold · {totalRemaining} remaining ·{" "}
                                {totalInventory} total
                              </p>
                            </div>
                          </div>

                          <div className="mt-6 space-y-3">
                            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                              Ticket tiers
                            </h3>

                            <div className="space-y-2">
                              {tierStats.map((tier) => (
                                <div
                                  key={tier.title}
                                  className="rounded-2xl bg-slate-50 px-4 py-3"
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div>
                                      <p className="text-sm font-semibold text-slate-900">
                                        {tier.title}
                                      </p>
                                      <p className="mt-1 text-xs text-slate-500">
                                        {tier.sold} sold · {tier.remaining} remaining
                                      </p>
                                    </div>

                                    <div className="text-right">
                                      <p className="text-sm font-semibold text-slate-900">
                                        ${Number(tier.price).toFixed(2)}
                                      </p>
                                      <p
                                        className={`mt-1 text-xs font-medium ${
                                          tier.isSoldOut
                                            ? "text-red-600"
                                            : "text-emerald-600"
                                        }`}
                                      >
                                        {tier.isSoldOut ? "Sold Out" : "Available"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="mt-8 grid gap-3 sm:grid-cols-2">
                          <button
                            onClick={() => {
                              setSelectedEventIdForTickets(event._id);
                              handleViewTickets(event._id);
                            }}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                          >
                            <Ticket className="h-4 w-4" />
                            View Tickets
                          </button>

                          <button
                            onClick={() => setShowScanner(true)}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
                          >
                            <ScanLine className="h-4 w-4" />
                            Scan Ticket
                          </button>

                          <button
                            onClick={() => setEditingEvent(event)}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-600"
                          >
                            <Pencil className="h-4 w-4" />
                            Edit Event
                          </button>

                          <button
                            onClick={() => handleDelete(event._id)}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete Event
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-slate-200 bg-slate-50/70 px-6 py-6 sm:px-8">
                      <div className="mb-4 flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">
                            Ticket List
                          </h3>
                          <p className="mt-1 text-sm text-slate-500">
                            View and manage tickets for this event.
                          </p>
                        </div>

                        <button
                          onClick={() => setSelectedEventIdForTickets(null)}
                          className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-100 hover:text-slate-900"
                        >
                          Close
                        </button>
                      </div>

                      <TicketList
                        eventId={event._id}
                        tickets={ticketsByEvent[event._id] || []}
                        onClose={() => setSelectedEventIdForTickets(null)}
                      />
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        {showScanner && <TicketScanner onClose={() => setShowScanner(false)} />}

        {editingEvent && (
          <EditEventModal
            event={editingEvent}
            onClose={() => setEditingEvent(null)}
            onSave={(updatedEvent) => {
              setEvents((prev) =>
                prev.map((e) =>
                  e._id === updatedEvent._id ? updatedEvent : e
                )
              );
              setEditingEvent(null);
            }}
          />
        )}
      </div>
    </main>
  );
}